import { useSuspenseQuery } from '@tanstack/react-query';
import { AxiosError, AxiosInstance } from 'axios';
import { Drug } from '../types/drug';

export type FormInfoResponse =
  | {
      kind: 'pandadoc';
      embedded_form_url: string;
      form_name?: string;
      allow_signature_upload: boolean;
      // Note: Only present for Prescriber forms
      drug?: Drug;
      supports_provider_attestation?: boolean;
    }
  | {
      kind: 'form_filler';
      role: 'Patient' | 'Prescriber';
      form_name?: string;
      allow_signature_upload: boolean;
      // Note: Only present for Prescriber forms
      drug?: Drug;
      supports_provider_attestation?: boolean;
    };

type FormInfo =
  | { kind: 'success'; info: FormInfoResponse }
  | { kind: 'notFound' }
  | { kind: 'error' }
  | { kind: 'revoked'; message: string };

export function useFormInfoForToken(args: { token: string | null; client: AxiosInstance }) {
  const { token, client } = args;

  const formInfo = useSuspenseQuery<FormInfo>({
    queryKey: ['formInfo', token],
    queryFn: async () => {
      if (token === null || token === '') {
        return { kind: 'notFound' } as const;
      }
      try {
        const response = await client.get(`/forms/${token}/get-form-info`);
        if (!response.data.kind) {
          return { kind: 'notFound' } as const;
        }
        return { kind: 'success', info: response.data } as const;
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          if (error.response?.status === 410) {
            const errorMessage = error.response.data?.detail || 'Link has expired';
            return { kind: 'revoked', message: errorMessage } as const;
          }
          if (error.response?.status === 404) {
            return { kind: 'notFound' } as const;
          }
        }
        throw new Error(`Error getting form info: ${error}`);
      }
    },
  });
  return formInfo;
}
